import React from "react";
import { Clock, DollarSign, AlarmClock, ShieldOff, ArrowDown } from "lucide-react";

const PAINS = [
  {
    icon: Clock,
    title: "Days (or weeks) of waiting",
    body: "Posters get stuck in queues with outside vendors while events approach.",
  },
  {
    icon: DollarSign,
    title: "High costs for banners & signage",
    body: "Recurring vendor markups quietly eat through your annual budget.",
  },
  {
    icon: AlarmClock,
    title: "Last-minute rush orders",
    body: "Pep rallies, tournaments, and ceremonies always seem to need print yesterday.",
  },
  {
    icon: ShieldOff,
    title: "Limited control over quality",
    body: "Color, finishing, and timing depend on someone outside your building.",
  },
];

const PAIN_IMG =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/a7p1pl3p_Students-Using-Posters-Created-with-a-School-Poster-Maker.webp";

export default function PainSection() {
  return (
    <section
      data-testid="pain-section"
      className="relative bg-[#F4F6F4] py-24 md:py-32 border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
              What Most Schools Are Dealing With
            </div>
            <h2
              data-testid="pain-headline"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-tight"
            >
              Most Schools Are Still Waiting on Outside Printing
            </h2>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-md border border-slate-200">
              <img
                src={PAIN_IMG}
                alt="Educator in a classroom — the hidden cost of outside print vendors"
                loading="lazy"
                decoding="async"
                className="w-full h-[260px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/65 via-navy/10 to-transparent" />
              <div className="absolute bottom-4 left-5 right-5">
                <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold text-white/85 mb-1">
                  Every campus, every week
                </span>
                <p className="font-serif text-white text-base sm:text-lg leading-snug">
                  "Can we get this banner before Friday?" — said by everyone, all year.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 max-w-5xl">
          {PAINS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`pain-item-${i}`}
                className="flex items-start gap-5 group"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-white border border-slate-200 flex items-center justify-center group-hover:border-teal group-hover:bg-teal/5 transition-colors">
                  <Icon className="w-6 h-6 text-navy group-hover:text-teal transition-colors" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="font-serif text-xl font-bold text-navy mb-1.5">
                    {p.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div
          data-testid="pain-shift-line"
          className="mt-20 pt-12 border-t border-slate-300 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <ArrowDown className="w-6 h-6 text-teal animate-bounce" />
          <p className="font-serif text-2xl sm:text-3xl font-bold text-navy italic">
            It doesn't have to work this way.
          </p>
        </div>
      </div>
    </section>
  );
}
